<template>
  <div class="agreement-container">
    <!-- 协议头部 -->
    <header class="agreement-header" v-if="parsedContent.title">
      <h1 class="agreement-title">{{ parsedContent.title }}</h1>
      <div class="agreement-meta" v-if="agreement.version || agreement.effective_date">
        <span class="version" v-if="agreement.version">版本: {{ agreement.version }}</span>
        <span class="effective-date" v-if="agreement.effective_date">
          生效日期: {{ formatDate(agreement.effective_date) }}
        </span>
      </div>
    </header>

    <!-- 动态生成的内容 -->
    <main class="agreement-content">
      <!-- 特别提示区域 -->
      <section class="special-notice" v-if="parsedContent.specialNotice">
        <div class="notice-card">
          <h2>特别提示</h2>
          <div class="notice-content" v-html="formatContent(parsedContent.specialNotice)"></div>
        </div>
      </section>

      <!-- 主要章节 -->
      <template v-for="(section, sectionIndex) in parsedContent.sections" :key="sectionIndex">
        <section :id="`section-${sectionIndex}`" class="agreement-section">
          <h2 class="section-title">
            {{ section.title || `第${this.getChineseNumber(sectionIndex + 1)}、` }}
          </h2>
          
          <!-- 章节内容 -->
          <div v-if="section.content" class="section-content" 
               v-html="formatContent(section.content)"></div>

          <!-- 子章节 -->
          <template v-for="(subsection, subIndex) in section.subsections" :key="subIndex">
            <div :id="`subsection-${sectionIndex}-${subIndex}`" class="subsection">
              <h3 class="subsection-title">
                {{ subsection.title || `${sectionIndex + 1}.${subIndex + 1}` }}
              </h3>
              <div class="subsection-content" v-html="formatContent(subsection.content)"></div>
            </div>
          </template>

          <!-- 无子章节的条款列表 -->
          <div v-if="section.items && section.items.length > 0" class="section-items">
            <div v-for="(item, itemIndex) in section.items" :key="itemIndex" class="agreement-item">
              <div class="item-content" v-html="formatContent(item)"></div>
            </div>
          </div>
        </section>
      </template>

      <!-- 协议尾部内容 -->
      <footer class="agreement-footer" v-if="parsedContent.footer">
        <div class="footer-content" v-html="formatContent(parsedContent.footer)"></div>
      </footer>
    </main>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DynamicAgreement',
  data() {
    return {
      agreement: {},
      parsedContent: {
        title: '',
        specialNotice: '',
        sections: [],
        footer: ''
      },
      readingProgress: 0
    }
  },
  async mounted() {
    await this.fetchAgreement()
    this.setupScrollListener()
  },
  methods: {
    async fetchAgreement() {
      try {
        const agreementId = this.$route.query.id
        const response = await axios.get(`/api/agreements/${agreementId}/`)
        this.agreement = response.data
        this.parseContent(this.agreement.content)
      } catch (error) {
        console.error('获取协议失败:', error)
      }
    },

    parseContent(content) {
      if (!content) return
      
      const lines = content.split('\n').filter(line => line.trim())
      let currentSection = null
      let currentSubsection = null
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        
        // 检测标题
        if (this.isTitle(line) && !this.parsedContent.title) {
          this.parsedContent.title = line
          continue
        }
        
        // 检测特别提示
        if (line.includes('特别提示')) {
          this.parsedContent.specialNotice = this.extractSectionContent(lines, i)
          continue
        }
        
        // 检测章节（一、二、三...或1、2、3...）
        if (this.isChapter(line)) {
          if (currentSection) {
            this.parsedContent.sections.push({...currentSection})
          }
          currentSection = {
            title: line,
            content: '',
            subsections: [],
            items: []
          }
          continue
        }
        
        // 检测子章节（1.1、2.1、3.1...）
        if (this.isSubsection(line)) {
          if (currentSubsection && currentSection) {
            currentSection.subsections.push({...currentSubsection})
          }
          currentSubsection = {
            title: line,
            content: ''
          }
          continue
        }
        
        // 检测列表项（•、-、数字等）
        if (this.isListItem(line)) {
          if (currentSection) {
            if (!currentSection.items) currentSection.items = []
            currentSection.items.push(line)
          }
          continue
        }
        
        // 添加到当前内容
        if (currentSubsection) {
          currentSubsection.content += line + '\n'
        } else if (currentSection) {
          currentSection.content += line + '\n'
        } else if (i > lines.length - 10) { // 尾部内容
          this.parsedContent.footer += line + '\n'
        }
      }
      
      // 添加最后一个章节
      if (currentSubsection && currentSection) {
        currentSection.subsections.push(currentSubsection)
      }
      if (currentSection) {
        this.parsedContent.sections.push(currentSection)
      }
    },

    // 内容检测方法
    isTitle(line) {
      return line.includes('服务协议') || line.includes('协议')
    },

    isChapter(line) {
      const chapterPattern = /^[一二三四五六七八九十]、|^[1-9][0-9]*、|^第[一二三四五六七八九十]/
      return chapterPattern.test(line)
    },

    isSubsection(line) {
      const subsectionPattern = /^[1-9][0-9]*\.[0-9]+|^[1-9][0-9]*\.[0-9]+\.[0-9]+/
      return subsectionPattern.test(line)
    },

    isListItem(line) {
      const listPattern = /^[•·\-*]\s|^\d+\.\s|^\(\d+\)\s/
      return listPattern.test(line) || line.startsWith('　') || line.startsWith('  ')
    },

    extractSectionContent(lines, startIndex) {
      let content = ''
      for (let i = startIndex + 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (this.isChapter(line) || line.includes('一、')) break
        content += line + '\n'
      }
      return content
    },

    // 内容格式化
    formatContent(content) {
      if (!content) return ''
      
      return content
        .replace(/\n/g, '<br>')
        .replace(/(^|\n)([•·\-*])\s/g, '$1<span class="list-marker">$2</span> ')
        .replace(/(^|\n)(\d+\.)\s/g, '$1<span class="number-marker">$2</span> ')
        .replace(/（([^）]+)）/g, '<span class="parentheses">（$1）</span>')
        .replace(/【([^】]+)】/g, '<span class="brackets">【$1】</span>')
    },

    getChineseNumber(num) {
      const chineseNumbers = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十']
      return num <= 10 ? chineseNumbers[num - 1] : num.toString()
    },

    // 滚动和导航
    scrollToSection(sectionIndex) {
      const element = document.getElementById(`section-${sectionIndex}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    },

    scrollToSubSection(sectionIndex, subIndex) {
      const element = document.getElementById(`subsection-${sectionIndex}-${subIndex}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    },

    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    setupScrollListener() {
      window.addEventListener('scroll', this.updateReadingProgress)
    },

    updateReadingProgress() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight
      this.readingProgress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0
    },

    handleAgree() {
      this.$emit('agreed', this.agreement.id)
    },

    handleDisagree() {
      this.$router.back()
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    }
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.updateReadingProgress)
  }
}
</script>

<style scoped>
.agreement-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  line-height: 1.8;
  color: #333;
  font-size: 14px;
}

.agreement-header {
  text-align: center;
}

.agreement-title {
  font-size: 22px;
  color: #000;
  font-weight: bold;
  line-height: 1.4;
}

.agreement-meta {
  color: #666;
  font-size: 12px;
}

.agreement-meta span {
  margin: 0 15px;
}

.notice-card h2 {
  color: #000;
  font-weight: bold;
  font-size: 16px;
  margin: 0 0 12px 0;
}

.notice-content {
  color: #333;
  text-align: justify;
  margin: 0 0 18px 0;
}

.section-title {
  color: #000;
  font-weight: bold;
  font-size: 16px;
  padding: 0;
}

.section-content {
  color: #333;
  text-align: justify;
}

.subsection {
  margin: 0 0 18px 0;
}

.subsection-title {
  color: #000;
  font-weight: bold;
  font-size: 14px;
  line-height: 1.4;
}

.subsection-content {
  color: #333;
  text-align: justify;
  line-height: 1.8;
}

.agreement-item {
  margin: 10px 0;
  padding: 0;
  text-align: justify;
}

:deep(.list-marker) {
  color: #000;
  font-weight: normal;
  margin-right: 5px;
}

:deep(.number-marker) {
  color: #000;
  font-weight: normal;
  margin-right: 5px;
}

:deep(.parentheses) {
  color: #333;
}

:deep(.brackets) {
  color: #333;
  font-weight: normal;
}

.agreement-footer {
  margin-top: 30px;
  padding-top: 20px;
  text-align: center;
  color: #666;
  font-size: 12px;
  line-height: 1.6;
}

.reading-progress {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 12px 20px;
  box-shadow: 0 -1px 3px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #e8e8e8;
}

.progress-bar {
  flex: 1;
  height: 3px;
  background: #f0f0f0;
  border-radius: 1px;
  margin-right: 20px;
}

.progress {
  height: 100%;
  background: #666;
  border-radius: 1px;
  transition: width 0.3s;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-primary, .btn-secondary, .btn-scroll-top {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  background: white;
  color: #333;
}

.btn-primary {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
}

.btn-primary:hover {
  background: #40a9ff;
  border-color: #40a9ff;
}

.btn-secondary:hover {
  background: #f5f5f5;
}

.btn-scroll-top:hover {
  background: #f5f5f5;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .agreement-container {
    padding: 15px;
    font-size: 13px;
  }
  
  .agreement-title {
    font-size: 18px;
  }
  
  .section-title {
    font-size: 15px;
  }
  
  .subsection-title {
    font-size: 13px;
  }
  
  .reading-progress {
    flex-direction: column;
    gap: 8px;
    padding: 10px 15px;
  }
  
  .progress-bar {
    margin-right: 0;
    margin-bottom: 8px;
    width: 100%;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: space-between;
  }
  
  .btn-primary, .btn-secondary, .btn-scroll-top {
    padding: 6px 12px;
    font-size: 11px;
  }
}

/* 打印样式 */
@media print {
  .reading-progress {
    display: none;
  }
  
  .agreement-container {
    max-width: none;
    padding: 0;
  }
}
</style>